import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/db/supabase';

type Language = 'en' | 'zh-CN' | 'zh-TW' | 'ja' | 'ko';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  formatDate: (date: Date | string, format?: 'short' | 'long') => string;
  formatNumber: (num: number, decimals?: number) => string;
  formatCurrency: (amount: number, currency?: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  'en': {
    // App & Navigation
    'app.name': 'Wanxiang Juju 2135',
    'nav.home': 'Home',
    'nav.profile': 'Profile',
    'nav.membership': 'Membership',
    'nav.tasks': 'Daily Tasks',
    'nav.achievements': 'Achievements',
    'nav.templateMarket': 'Template Market',
    'nav.admin': 'Admin',
    'nav.logout': 'Logout',
    'nav.login': 'Login',
    'nav.register': 'Register',
    
    // Common Actions
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.submit': 'Submit',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.share': 'Share',
    'common.like': 'Like',
    'common.comment': 'Comment',
    'common.points': 'Points',
    'common.success': 'Success',
    'common.error': 'Error',
    'common.warning': 'Warning',
    'common.info': 'Info',
    'common.close': 'Close',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.viewMore': 'View More',
    'common.viewLess': 'View Less',
    
    // Achievement System
    'achievement.wall': 'Achievement Wall',
    'achievement.unlocked': 'Unlocked',
    'achievement.locked': 'Locked',
    'achievement.progress': 'Progress',
    'achievement.checkProgress': 'Check Progress',
    'achievement.totalUnlocked': 'Total Unlocked',
    'achievement.totalPoints': 'Total Points',
    'achievement.completionRate': 'Completion Rate',
    'achievement.category.all': 'All',
    'achievement.category.social': 'Social',
    'achievement.category.content': 'Content',
    'achievement.category.learning': 'Learning',
    'achievement.category.special': 'Special',
    'achievement.reward': 'Reward',
    'achievement.shareToComm': 'Share to Community',
    'achievement.generatePoster': 'Generate Poster',
    
    // Template Market
    'template.market': 'Template Market',
    'template.purchase': 'Purchase',
    'template.review': 'Review',
    'template.rating': 'Rating',
    'template.price': 'Price',
    'template.myTemplates': 'My Templates',
    'template.uploadTemplate': 'Upload Template',
    'template.downloads': 'Downloads',
    'template.revenue': 'Revenue',
    'template.category': 'Category',
    'template.sortBy': 'Sort By',
    'template.sortByLikes': 'Most Liked',
    'template.sortByRating': 'Highest Rated',
    'template.sortByTime': 'Latest',
    'template.reviewPlaceholder': 'Share your experience...',
    'template.selectTags': 'Select Tags',
    'template.submitReview': 'Submit Review',
    
    // Daily Tasks
    'task.daily': 'Daily Tasks',
    'task.checkIn': 'Check In',
    'task.completed': 'Completed',
    'task.inProgress': 'In Progress',
    'task.reward': 'Reward',
    'task.todayProgress': 'Today\'s Progress',
    'task.completedTasks': 'Completed Tasks',
    'task.totalRewards': 'Total Rewards',
    
    // Settings
    'settings.language': 'Language',
    'settings.theme': 'Theme',
    'settings.notifications': 'Notifications',
    'settings.emailNotifications': 'Email Notifications',
    'settings.preferences': 'Preferences',
    
    // Poster Generator
    'poster.title': 'Achievement Poster Generator',
    'poster.template': 'Template',
    'poster.tech': 'Tech Style',
    'poster.minimal': 'Minimal Style',
    'poster.colorful': 'Colorful Style',
    'poster.background': 'Background',
    'poster.signature': 'Signature',
    'poster.motto': 'Motto',
    'poster.stickers': 'Stickers',
    'poster.maxStickers': 'Max 5 stickers',
    'poster.download': 'Download Poster',
    'poster.shareToComm': 'Share to Community',
    'poster.generating': 'Generating...',
    
    // Error Messages
    'error.loginRequired': 'Please login first',
    'error.insufficientPoints': 'Insufficient points',
    'error.networkError': 'Network error, please try again',
    'error.uploadFailed': 'Upload failed',
    'error.deleteFailed': 'Delete failed',
    'error.updateFailed': 'Update failed',
    
    // Success Messages
    'success.saved': 'Saved successfully',
    'success.uploaded': 'Uploaded successfully',
    'success.deleted': 'Deleted successfully',
    'success.purchased': 'Purchased successfully',
    'success.shared': 'Shared successfully',
  },
  'zh-CN': {
    // App & Navigation
    'app.name': '万象聚聚光2135',
    'nav.home': '首页',
    'nav.profile': '个人中心',
    'nav.membership': '会员中心',
    'nav.tasks': '每日任务',
    'nav.achievements': '成就墙',
    'nav.templateMarket': '模板市场',
    'nav.admin': '后台管理',
    'nav.logout': '退出登录',
    'nav.login': '登录',
    'nav.register': '注册',
    
    // Common Actions
    'common.loading': '加载中...',
    'common.save': '保存',
    'common.cancel': '取消',
    'common.confirm': '确认',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.submit': '提交',
    'common.search': '搜索',
    'common.filter': '筛选',
    'common.sort': '排序',
    'common.download': '下载',
    'common.upload': '上传',
    'common.share': '分享',
    'common.like': '点赞',
    'common.comment': '评论',
    'common.points': '积分',
    'common.success': '成功',
    'common.error': '错误',
    'common.warning': '警告',
    'common.info': '提示',
    'common.close': '关闭',
    'common.back': '返回',
    'common.next': '下一步',
    'common.previous': '上一步',
    'common.viewMore': '查看更多',
    'common.viewLess': '收起',
    
    // Achievement System
    'achievement.wall': '成就墙',
    'achievement.unlocked': '已解锁',
    'achievement.locked': '未解锁',
    'achievement.progress': '进度',
    'achievement.checkProgress': '检查成就进度',
    'achievement.totalUnlocked': '已解锁成就',
    'achievement.totalPoints': '成就积分',
    'achievement.completionRate': '完成度',
    'achievement.category.all': '全部',
    'achievement.category.social': '社交互动',
    'achievement.category.content': '内容创作',
    'achievement.category.learning': '学习成长',
    'achievement.category.special': '特殊成就',
    'achievement.reward': '奖励',
    'achievement.shareToComm': '分享到社区',
    'achievement.generatePoster': '生成海报',
    
    // Template Market
    'template.market': '模板市场',
    'template.purchase': '购买',
    'template.review': '评价',
    'template.rating': '评分',
    'template.price': '价格',
    'template.myTemplates': '我的模板',
    'template.uploadTemplate': '上传模板',
    'template.downloads': '下载量',
    'template.revenue': '收益',
    'template.category': '分类',
    'template.sortBy': '排序方式',
    'template.sortByLikes': '最多点赞',
    'template.sortByRating': '最高评分',
    'template.sortByTime': '最新发布',
    'template.reviewPlaceholder': '分享你对这个模板的使用体验...',
    'template.selectTags': '选择标签',
    'template.submitReview': '提交评价',
    
    // Daily Tasks
    'task.daily': '每日任务',
    'task.checkIn': '签到',
    'task.completed': '已完成',
    'task.inProgress': '进行中',
    'task.reward': '奖励',
    'task.todayProgress': '今日进度',
    'task.completedTasks': '已完成任务',
    'task.totalRewards': '累计奖励',
    
    // Settings
    'settings.language': '语言',
    'settings.theme': '主题',
    'settings.notifications': '通知',
    'settings.emailNotifications': '邮件通知',
    'settings.preferences': '偏好设置',
    
    // Poster Generator
    'poster.title': '成就海报生成器',
    'poster.template': '模板',
    'poster.tech': '科技风',
    'poster.minimal': '简约风',
    'poster.colorful': '炫彩风',
    'poster.background': '背景颜色',
    'poster.signature': '个人签名',
    'poster.motto': '座右铭',
    'poster.stickers': '贴纸装饰',
    'poster.maxStickers': '最多选择 5 个贴纸',
    'poster.download': '下载海报',
    'poster.shareToComm': '分享到社区',
    'poster.generating': '生成中...',
    
    // Error Messages
    'error.loginRequired': '请先登录',
    'error.insufficientPoints': '积分不足',
    'error.networkError': '网络错误，请重试',
    'error.uploadFailed': '上传失败',
    'error.deleteFailed': '删除失败',
    'error.updateFailed': '更新失败',
    
    // Success Messages
    'success.saved': '保存成功',
    'success.uploaded': '上传成功',
    'success.deleted': '删除成功',
    'success.purchased': '购买成功',
    'success.shared': '分享成功',
  },
  'zh-TW': {
    'app.name': '萬象聚聚光2135',
    'nav.home': '首頁',
    'nav.profile': '個人中心',
    'nav.membership': '會員中心',
    'nav.tasks': '每日任務',
    'nav.achievements': '成就牆',
    'nav.templateMarket': '模板市場',
    'nav.admin': '後台管理',
    'nav.logout': '退出登錄',
    'common.loading': '加載中...',
    'common.save': '保存',
    'common.cancel': '取消',
    'common.confirm': '確認',
    'common.delete': '刪除',
    'common.edit': '編輯',
    'common.submit': '提交',
    'common.search': '搜索',
    'common.filter': '篩選',
    'common.sort': '排序',
    'common.download': '下載',
    'common.upload': '上傳',
    'common.share': '分享',
    'common.like': '點贊',
    'common.comment': '評論',
    'common.points': '積分',
    'achievement.wall': '成就牆',
    'achievement.unlocked': '已解鎖',
    'achievement.locked': '未解鎖',
    'achievement.progress': '進度',
    'achievement.checkProgress': '檢查成就進度',
    'template.market': '模板市場',
    'template.purchase': '購買',
    'template.review': '評價',
    'template.rating': '評分',
    'template.price': '價格',
    'task.daily': '每日任務',
    'task.checkIn': '簽到',
    'task.completed': '已完成',
    'settings.language': '語言',
    'settings.theme': '主題',
  },
  'ja': {
    'app.name': 'Wanxiang Juju 2135',
    'nav.home': 'ホーム',
    'nav.profile': 'プロフィール',
    'nav.membership': 'メンバーシップ',
    'nav.tasks': 'デイリータスク',
    'nav.achievements': 'アチーブメント',
    'nav.templateMarket': 'テンプレート市場',
    'nav.admin': '管理',
    'nav.logout': 'ログアウト',
    'common.loading': '読み込み中...',
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.confirm': '確認',
    'common.delete': '削除',
    'common.edit': '編集',
    'common.submit': '送信',
    'common.search': '検索',
    'common.filter': 'フィルター',
    'common.sort': '並び替え',
    'common.download': 'ダウンロード',
    'common.upload': 'アップロード',
    'common.share': '共有',
    'common.like': 'いいね',
    'common.comment': 'コメント',
    'common.points': 'ポイント',
    'achievement.wall': 'アチーブメントウォール',
    'achievement.unlocked': 'アンロック済み',
    'achievement.locked': 'ロック中',
    'achievement.progress': '進捗',
    'achievement.checkProgress': '進捗を確認',
    'template.market': 'テンプレート市場',
    'template.purchase': '購入',
    'template.review': 'レビュー',
    'template.rating': '評価',
    'template.price': '価格',
    'task.daily': 'デイリータスク',
    'task.checkIn': 'チェックイン',
    'task.completed': '完了',
    'settings.language': '言語',
    'settings.theme': 'テーマ',
  },
  'ko': {
    'app.name': 'Wanxiang Juju 2135',
    'nav.home': '홈',
    'nav.profile': '프로필',
    'nav.membership': '멤버십',
    'nav.tasks': '일일 작업',
    'nav.achievements': '업적',
    'nav.templateMarket': '템플릿 마켓',
    'nav.admin': '관리',
    'nav.logout': '로그아웃',
    'common.loading': '로딩 중...',
    'common.save': '저장',
    'common.cancel': '취소',
    'common.confirm': '확인',
    'common.delete': '삭제',
    'common.edit': '편집',
    'common.submit': '제출',
    'common.search': '검색',
    'common.filter': '필터',
    'common.sort': '정렬',
    'common.download': '다운로드',
    'common.upload': '업로드',
    'common.share': '공유',
    'common.like': '좋아요',
    'common.comment': '댓글',
    'common.points': '포인트',
    'achievement.wall': '업적 벽',
    'achievement.unlocked': '잠금 해제됨',
    'achievement.locked': '잠김',
    'achievement.progress': '진행률',
    'achievement.checkProgress': '진행률 확인',
    'template.market': '템플릿 마켓',
    'template.purchase': '구매',
    'template.review': '리뷰',
    'template.rating': '평가',
    'template.price': '가격',
    'task.daily': '일일 작업',
    'task.checkIn': '체크인',
    'task.completed': '완료',
    'settings.language': '언어',
    'settings.theme': '테마',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('zh-CN');

  useEffect(() => {
    const loadUserPreferences = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase.rpc('get_or_create_user_preferences');
        if (data && data.language) {
          setLanguageState(data.language as Language);
        }
      } else {
        // 未登录用户从 localStorage 读取
        const savedLang = localStorage.getItem('language') as Language;
        if (savedLang) {
          setLanguageState(savedLang);
        }
      }
    };
    loadUserPreferences();
  }, []);

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);

    // 如果用户已登录，更新数据库
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.rpc('update_user_preferences', { language_param: lang });
    }
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const formatDate = (date: Date | string, format: 'short' | 'long' = 'short'): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    const locales: Record<Language, string> = {
      'en': 'en-US',
      'zh-CN': 'zh-CN',
      'zh-TW': 'zh-TW',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
    };

    const options: Intl.DateTimeFormatOptions = format === 'long'
      ? { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }
      : { year: 'numeric', month: '2-digit', day: '2-digit' };

    return d.toLocaleDateString(locales[language], options);
  };

  const formatNumber = (num: number, decimals = 0): string => {
    const locales: Record<Language, string> = {
      'en': 'en-US',
      'zh-CN': 'zh-CN',
      'zh-TW': 'zh-TW',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
    };

    return num.toLocaleString(locales[language], {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatCurrency = (amount: number, currency = 'USD'): string => {
    const locales: Record<Language, string> = {
      'en': 'en-US',
      'zh-CN': 'zh-CN',
      'zh-TW': 'zh-TW',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
    };

    return amount.toLocaleString(locales[language], {
      style: 'currency',
      currency,
    });
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, formatDate, formatNumber, formatCurrency }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
